import { CURRENT_SCHEMA_VERSION, recordKey, type StudyRecord, type StudyRecordType } from './types';
import { createId, isoNow } from './crypto';

const RECORD_TYPES = new Set<StudyRecordType>([
  'profile', 'study-plan', 'assignment', 'study-block', 'study-session', 'task-completion',
  'practice-attempt', 'daily-report', 'mistake', 'review-event', 'oral-quiz',
  'case-submission', 'coach-feedback', 'activity-event', 'plan-revision', 'plan-day',
]);

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const validIso = (value: unknown): value is string =>
  typeof value === 'string' && Number.isFinite(Date.parse(value));

export function createStudyRecord<TPayload extends Record<string, unknown>>(input: {
  id?: string;
  type: StudyRecordType;
  payload: TPayload;
  deviceId: string;
  studyDate?: string;
  source?: StudyRecord['source'];
}): StudyRecord<TPayload> {
  const now = isoNow();
  const id = input.id ?? createId(input.type);
  return {
    key: recordKey(input.type, id),
    id,
    type: input.type,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    revision: 1,
    createdAt: now,
    updatedAt: now,
    deviceId: input.deviceId,
    source: input.source ?? 'app',
    studyDate: input.studyDate,
    payload: input.payload,
  };
}

export function normalizeStudyRecord(value: unknown, fallbackDeviceId: string): StudyRecord {
  if (!isObject(value)) throw new Error('记录必须是对象');
  if (typeof value.type !== 'string' || !RECORD_TYPES.has(value.type as StudyRecordType)) {
    throw new Error(`未知记录类型：${String(value.type)}`);
  }
  const type = value.type as StudyRecordType;
  const id = typeof value.id === 'string' && value.id ? value.id : createId(type);
  const schemaVersion = Number(value.schemaVersion ?? 1);
  if (!Number.isInteger(schemaVersion) || schemaVersion < 1 || schemaVersion > CURRENT_SCHEMA_VERSION) {
    throw new Error(`不支持的记录版本：${schemaVersion}`);
  }
  const now = isoNow();
  const migratedPayload = isObject(value.payload) ? value.payload : {};
  const normalized: StudyRecord = {
    key: recordKey(type, id),
    id,
    type,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    revision: Math.max(1, Number(value.revision) || 1),
    createdAt: validIso(value.createdAt) ? value.createdAt : now,
    updatedAt: validIso(value.updatedAt) ? value.updatedAt : now,
    deviceId: typeof value.deviceId === 'string' && value.deviceId ? value.deviceId : fallbackDeviceId,
    source: ['app', 'coach', 'legacy-localStorage', 'import', 'remote'].includes(String(value.source))
      ? value.source as StudyRecord['source']
      : 'import',
    payload: migratedPayload,
  };
  if (validIso(value.deletedAt)) normalized.deletedAt = value.deletedAt;
  if (typeof value.studyDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.studyDate)) {
    normalized.studyDate = value.studyDate;
  }
  return normalized;
}

export function assertPlainObject(value: unknown, label: string): asserts value is Record<string, unknown> {
  if (!isObject(value)) throw new Error(`${label}必须是对象`);
}

export function assertSafeCollectionSize(label: string, size: number, maximum: number): void {
  if (!Number.isInteger(size) || size < 0 || size > maximum) {
    throw new Error(`${label}数量异常：${size}，上限 ${maximum}`);
  }
}
